import { forwardRef, useCallback, useEffect } from "react";

import { Icon } from "../icon/Icon";
import { MediaPlayerFullscreenWindowPosition } from "../media/types";
import { Popover } from "../popover/Popover";
import { Box } from "../box/Box";
import { Button } from "../button/Button";

export const WINDOW_POSITION_OPTIONS: Array<MediaPlayerFullscreenWindowPosition> =
  [
    MediaPlayerFullscreenWindowPosition.bottomLeft,
    MediaPlayerFullscreenWindowPosition.bottomRight,
    MediaPlayerFullscreenWindowPosition.topLeft,
    MediaPlayerFullscreenWindowPosition.topRight,
    MediaPlayerFullscreenWindowPosition.hidden,
  ];
export const WINDOW_POSITION_LABELS = new Map([
  [MediaPlayerFullscreenWindowPosition.bottomLeft, "Bottom Left"],
  [MediaPlayerFullscreenWindowPosition.bottomRight, "Bottom Right"],
  [MediaPlayerFullscreenWindowPosition.topLeft, "Top Left"],
  [MediaPlayerFullscreenWindowPosition.topRight, "Top Right"],
  [MediaPlayerFullscreenWindowPosition.hidden, "Hide"],
]);

export interface VideoControlWindowPositionProps {
  value?: MediaPlayerFullscreenWindowPosition;
  onChange?(value: MediaPlayerFullscreenWindowPosition): void;
  onActivate?: () => void;
}

export function getIconName(
  value: MediaPlayerFullscreenWindowPosition = MediaPlayerFullscreenWindowPosition.bottomLeft,
): string {
  switch (value) {
    case MediaPlayerFullscreenWindowPosition.hidden:
      return "pip-fullscreen-xmark-bottom-left";
    case MediaPlayerFullscreenWindowPosition.bottomLeft:
      return "pip-fullscreen-bottom-left";
    case MediaPlayerFullscreenWindowPosition.bottomRight:
      return "pip-fullscreen-bottom-right";
    case MediaPlayerFullscreenWindowPosition.topLeft:
      return "pip-fullscreen-top-left";
    case MediaPlayerFullscreenWindowPosition.topRight:
      return "pip-fullscreen-top-right";
    default:
      return "pip-fullscreen-bottom-left";
  }
}

interface VideoControlWindowPositionOptionsProps {
  close?(): void;
  onActivate?(): void;
  onChange?(windowPosition: MediaPlayerFullscreenWindowPosition): void;
}

const VideoControlWindowPositionOptions = ({
  close,
  onActivate,
  onChange,
}: VideoControlWindowPositionOptionsProps) => {
  useEffect(() => {
    onActivate?.();
  }, [onActivate]);

  const handleClick = useCallback(
    (windowPosition: MediaPlayerFullscreenWindowPosition) => {
      return () => {
        onChange?.(windowPosition);
        close?.();
      };
    },
    [close, onChange],
  );

  return (
    <Box direction="column" align="start" gap={2}>
      {WINDOW_POSITION_OPTIONS.map((windowPosition) => (
        <Button
          icon={getIconName(windowPosition)}
          key={getIconName(windowPosition)}
          appearance="minimal"
          size="xs"
          onClick={handleClick(windowPosition)}
        >
          {WINDOW_POSITION_LABELS.get(windowPosition)}
        </Button>
      ))}
    </Box>
  );
};

export const VideoControlWindowPosition = forwardRef<
  HTMLButtonElement,
  VideoControlWindowPositionProps
>(function VideoControlWindowPositionImpl(
  { value, onChange, onActivate },
  ref,
) {
  return (
    <Popover strategy="fixed" placement="bottom" offset={-12} elevation={2}>
      <Popover.Trigger>
        <button ref={ref} className="VideoControl">
          <Icon size="lg" icon={getIconName(value)} />
        </button>
      </Popover.Trigger>

      <Popover.Panel>
        {({ close }) => {
          return (
            <VideoControlWindowPositionOptions
              close={close}
              onChange={onChange}
              onActivate={onActivate}
            />
          );
        }}
      </Popover.Panel>
    </Popover>
  );
});
